import { useMutation, useQuery } from '@tanstack/react-query';

import competencyService, {
    type CompetencyGroupPayload,
    type CompetencyImpact,
    type CompetencyPayload,
} from './competencyService';

import type { CompetencyGroup, CompetencyStatusType } from '@/types/competency/Competency';

import { invalidateCompetencyTranslationQueries } from '@/api/competency-services/translation';
import { queryClient } from '@/AppWithProviders';
import { CompetencyProfileQueryKey, CompetencyQueryKey } from '@/constants/QueryKeys';

const invalidateCompetencyGroups = () => {
    queryClient.invalidateQueries({
        queryKey: [CompetencyQueryKey.COMPETENCY_GROUPS],
    });
    invalidateCompetencyTranslationQueries();
};

// Deactivating or removing a competency rewires the cascade across profile items
// and employee assignments, so the Profils tab must refetch as well.
const invalidateCompetencyCascade = () => {
    invalidateCompetencyGroups();

    queryClient.invalidateQueries({
        queryKey: [CompetencyProfileQueryKey.COMPETENCY_PROFILES],
    });
};

const useGetCompetencyGroups = (page = 1, pageSize = 50, enabled = true) => {
    return useQuery<CompetencyGroup[]>({
        queryKey: [CompetencyQueryKey.COMPETENCY_GROUPS, page, pageSize],
        queryFn: async () => {
            const paginated = await competencyService.getCompetencyGroups(page, pageSize);
            return paginated.items;
        },
        enabled,
    });
};

// Read-only observer on the already-loaded competency tree. For consumers rendered
// INSIDE the groups list: the page unmounts that list while the groups query is
// fetching, so a child whose mount triggers its own refetch (default staleTime 0)
// unmount/remounts itself in a loop. This observer never initiates a refetch (it
// still fetches once if the cache is empty) while staying subscribed to updates.
const useWatchCompetencyGroups = (page = 1, pageSize = 50) => {
    return useQuery<CompetencyGroup[]>({
        queryKey: [CompetencyQueryKey.COMPETENCY_GROUPS, page, pageSize],
        queryFn: async () => {
            const paginated = await competencyService.getCompetencyGroups(page, pageSize);
            return paginated.items;
        },
        staleTime: Infinity,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });
};

const useCreateCompetencyGroup = () => {
    return useMutation({
        mutationFn: (payload: CompetencyGroupPayload) =>
            competencyService.createCompetencyGroup(payload),
        onSuccess: invalidateCompetencyGroups,
    });
};

const useUpdateCompetencyGroup = () => {
    return useMutation({
        mutationFn: ({ id, payload }: { id: string; payload: CompetencyGroupPayload }) =>
            competencyService.updateCompetencyGroup(id, payload),
        onSuccess: invalidateCompetencyGroups,
    });
};

const useRemoveCompetencyGroup = () => {
    return useMutation({
        mutationFn: ({
            id,
            replacements,
        }: {
            id: string;
            replacements: Record<string, string | undefined>;
        }) => competencyService.removeCompetencyGroup(id, replacements),
        onSuccess: invalidateCompetencyCascade,
    });
};

const useDeactivateCompetencyGroup = () => {
    return useMutation({
        mutationFn: ({
            id,
            replacements,
        }: {
            id: string;
            replacements: Record<string, string | undefined>;
        }) => competencyService.deactivateCompetencyGroup(id, replacements),
        onSuccess: invalidateCompetencyCascade,
    });
};

const useCreateCompetency = () => {
    return useMutation({
        mutationFn: (payload: CompetencyPayload) => competencyService.createCompetency(payload),
        onSuccess: invalidateCompetencyGroups,
    });
};

const useUpdateCompetency = () => {
    return useMutation({
        mutationFn: ({
            id,
            payload,
        }: {
            id: string;
            payload: Omit<CompetencyPayload, 'competencyGroupId'>;
        }) => competencyService.updateCompetency(id, payload),
        onSuccess: invalidateCompetencyGroups,
    });
};

const useGetCompetencyImpact = (id: string, enabled = true) => {
    return useQuery<CompetencyImpact>({
        queryKey: [CompetencyQueryKey.COMPETENCY_IMPACT, id],
        queryFn: () => competencyService.getCompetencyImpact(id),
        enabled,
    });
};

const useDeactivateCompetency = () => {
    return useMutation({
        mutationFn: ({
            id,
            replacementCompetencyId,
        }: {
            id: string;
            replacementCompetencyId: string | null;
        }) => competencyService.deactivateCompetency(id, replacementCompetencyId),
        onSuccess: invalidateCompetencyCascade,
    });
};

const useRemoveCompetency = () => {
    return useMutation({
        mutationFn: ({
            id,
            replacementCompetencyId,
        }: {
            id: string;
            replacementCompetencyId: string | null;
        }) => competencyService.removeCompetency(id, replacementCompetencyId),
        onSuccess: invalidateCompetencyCascade,
    });
};

const useMoveCompetency = () => {
    return useMutation({
        mutationFn: ({
            id,
            targetCompetencyGroupId,
        }: {
            id: string;
            targetCompetencyGroupId: string;
        }) => competencyService.moveCompetency(id, targetCompetencyGroupId),
        onSuccess: invalidateCompetencyGroups,
    });
};

const useChangeCompetencyStatus = () => {
    return useMutation({
        mutationFn: ({ id, status }: { id: string; status: CompetencyStatusType }) =>
            competencyService.changeCompetencyStatus(id, status),
        onSuccess: invalidateCompetencyGroups,
    });
};

const useChangeCompetencyGroupStatus = () => {
    return useMutation({
        mutationFn: ({ id, status }: { id: string; status: CompetencyStatusType }) =>
            competencyService.changeCompetencyGroupStatus(id, status),
        onSuccess: invalidateCompetencyGroups,
    });
};

export {
    useGetCompetencyGroups,
    useWatchCompetencyGroups,
    useCreateCompetencyGroup,
    useUpdateCompetencyGroup,
    useRemoveCompetencyGroup,
    useDeactivateCompetencyGroup,
    useCreateCompetency,
    useUpdateCompetency,
    useGetCompetencyImpact,
    useDeactivateCompetency,
    useRemoveCompetency,
    useMoveCompetency,
    useChangeCompetencyStatus,
    useChangeCompetencyGroupStatus,
};
